import React, { SyntheticEvent, useEffect, useState } from "react";

import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import { Moment } from "moment";

import EditorColumn from "react-lib/appcon/common/EditorColumn";

import LoadingIcon from "../HCU/LoadingIcon";

import CardTimeShiftCell from "./CardTimeShiftCell";
import HolidayPopup, { HolidayResult, HolidaysType } from "./HolidayPopup";
import TableTotalSlotsCell from "./TableTotalSlotsCell";

import {
  ApplyNewTimeParamsType,
  ApproveTimeParamsType,
  BTN_ACTS,
  calculateShiftTimeSummary,
  calculateTotalAttendance,
  calculateWorkingHours,
  ClockRecordParamsType,
  ClockRecordType,
  EditModeType,
  EditRegularWorkParams,
  EventsTimeShiftType,
  groupEventsByCodeOrTime,
  hasHistoryForDay,
  HOLIDAY_TAG,
  OpenHistoryParamsType,
  PayrollRecordEmployeeType,
  WorkShiftType,
} from "./sequence/SetupWorkSchedule";

import { setupElementResize } from "../common/CommonInterface";
import { abbreviateThaiName } from "./HRMInterface";

type PatternType = "none" | "onlydate" | "tillEndMonth" | "tillEndYear";

export type PopupType = {
  action: "ADD" | "DEL";
  date: Moment | null;
  detail: string;
  pattern: PatternType;
};

type TableTimeShiftProps = {
  clockRecords?: ClockRecordType[];
  data?: WorkShiftType[];
  holidays?: HolidaysType[];
  isViewShiftCodeSummary?: boolean;
  listMonths: Moment[];
  payrollRecordEmployeeIds: PayrollRecordEmployeeType[];
  selectedMode?: EditModeType;
  showSummaryColumn: boolean;
  buttonLoadCheck?: any;
  onApplyNewTime?: (data: ApplyNewTimeParamsType) => void;
  onApproveTime?: (data: ApproveTimeParamsType) => void;
  onEditHoliday?: (h: HolidayResult) => void;
  onEditRegularWork?: (data: EditRegularWorkParams) => void;
  onGetClockRecord?: (data: ClockRecordParamsType) => void;
  onOpenHistory?: (data: OpenHistoryParamsType) => void;
  onRemoveEmployee?: (employeeId: number) => void;
  onSelectEvent?: (event: EventsTimeShiftType, e: SyntheticEvent) => void;
  onSelectSlot?: (slotInfo: SlotInfoType) => void;
};

export type SlotInfoType = {
  action?: "click";
  data?: Record<string, any>;
  end: Moment;
  slots?: Moment[];
  start: Moment;
};

// MOCK
const INITIAL_HOLIDAY: PopupType = { action: "ADD", date: null, detail: "", pattern: "none" };

const TableTimeShift = (props: TableTimeShiftProps) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [detailHoliday, setDetailHoliday] = useState<PopupType>(INITIAL_HOLIDAY);

  useEffect(() => {
    const resizer = setupElementResize({
      observerSelector: "#TabPenta > div",
      padding: 20,
      targetSelector: ".view-summary.total-slot .events-list",
    });

    if (props.isViewShiftCodeSummary) {
      resizer.initialize();
    }

    return () => {
      resizer.disconnect();
    };
  }, [props.data, props.isViewShiftCodeSummary]);

  const handleClosePopupHolidaySetup = () => {
    setOpenPopup(false);
    setDetailHoliday(INITIAL_HOLIDAY);
  };

  const handleTogglePopupHoliday = (day: Moment) => {
    const isSameDay = detailHoliday.date?.isSame(day, "day");

    if (openPopup && isSameDay) {
      handleClosePopupHolidaySetup();
    } else {
      setOpenPopup(true);
      setDetailHoliday({ ...detailHoliday, date: day });
    }
  };

  const handleConfirm = (result: HolidayResult) => {
    props.onEditHoliday?.(result);

    handleClosePopupHolidaySetup();
  };

  console.log("TableTimeShift props:", props);

  return (
    <Table celled>
      <TableHeader>
        <TableRow>
          <TableHeaderCell style={{ maxWidth: "125px", minWidth: "125px" }}>
            Employee
          </TableHeaderCell>
          {props.listMonths.map((day) => {
            const dayOfWeek = day.day();
            const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;
            const isHoliday = (props.holidays ?? []).some(
              (index: HolidaysType) => index.title === HOLIDAY_TAG && index.start.isSame(day, "day")
            );
            const classes = [isWeekend && "weekend", isHoliday && "holiday"]
              .filter(Boolean)
              .join(" ");

            const dayOfWeekText = day.clone().locale("TH").format("ddd");

            return (
              <TableHeaderCell
                key={`header-${day.format("YYYY-MM-DD")}`}
                id={`TableTimeShift-TableHeaderCell-${day.format("YYYY-MM-DD")}`}
                className={classes}
                style={{
                  cursor: "pointer",
                  ...(props.isViewShiftCodeSummary
                    ? { maxWidth: "82.5px", minWidth: "82.5px", width: "82.5px" }
                    : { maxWidth: "93.75px", minWidth: "93.75px", width: "93.75px" }),
                  ...(!props.showSummaryColumn && {
                    paddingBottom: "0.25rem",
                    paddingTop: "0.25rem",
                  }),
                }}
                onClick={() => {
                  handleTogglePopupHoliday(day);
                }}
              >
                <HolidayPopup
                  id={`TableTimeShift-HolidayPopup-${day.format("YYYY-MM-DD")}`}
                  dayOfWeekText={dayOfWeekText}
                  holidays={props.holidays}
                  open={openPopup ? !!detailHoliday.date?.isSame(day, "day") : false}
                  selectedDate={detailHoliday.date}
                  onClose={handleClosePopupHolidaySetup}
                  onConfirm={handleConfirm}
                  trigger={
                    <div>
                      {day.format("ddd")}
                      {"\n"}
                      {day.format(" DD")}{" "}
                    </div>
                  }
                />
              </TableHeaderCell>
            );
          })}
          {props.showSummaryColumn ? (
            <>
              <TableHeaderCell className="summary-column">
                Off
                <br />
                (day)
              </TableHeaderCell>
              <TableHeaderCell className="summary-column">
                Actual work
                <br />
                (hr.)
              </TableHeaderCell>
              <TableHeaderCell className="summary-column">
                Leave
                <br />
                (hr.)
              </TableHeaderCell>
              <TableHeaderCell className="summary-column">
                Regular work
                <br />
                (hr.)
              </TableHeaderCell>
              <TableHeaderCell className="summary-column" colSpan="2">
                OT
                <br />
                (hr.)
              </TableHeaderCell>
              <TableHeaderCell className="summary-column">
                Deficit
                <br /> (hr.)
              </TableHeaderCell>{" "}
            </>
          ) : null}
        </TableRow>
      </TableHeader>

      <TableBody>
        {(props.data ?? []).length === 0 ? (
          <TableCell
            style={{ color: "grey", padding: "2rem 0" }}
            colSpan={props.listMonths.length / 2}
            textAlign="center"
          >
            No rows found
          </TableCell>
        ) : null}

        {(props.data ?? []).map((staff) => {
          const groupedEvents = groupEventsByCodeOrTime(staff.events);

          const shiftSummary = calculateShiftTimeSummary(staff.events);

          const otHours =
            shiftSummary.approve.actualWorkHours +
            shiftSummary.approve.leaveHours -
            (staff.regular_work_hours ?? 0);

          const totalOvertimeHours =
            shiftSummary.approve.OTclock1hr +
            shiftSummary.approve.OTclock1_5hr +
            shiftSummary.approve.OTclock3hr;
          const sumOT = Number(totalOvertimeHours.toFixed(2));

          const totalSlots = groupedEvents.reduce((result, event) => result + event.total, 0);
          const disabledRemove = props.payrollRecordEmployeeIds.some(
            (item) => item.employee === staff.employee
          );

          return (
            <React.Fragment key={`staff-${staff.employee}`}>
              <TableRow className={props.isViewShiftCodeSummary ? "view-summary" : ""}>
                {/** (1) Employee Row */}
                <TableCell className="vertical-align-top">
                  <div
                    id={`TableTimeShift-Div-Employee-${staff.employee_code}`}
                    style={{ lineHeight: 1.2 }}
                  >
                    {!props.isViewShiftCodeSummary && <div>({staff.employee_code})</div>}
                    <div
                      style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {abbreviateThaiName(staff.employee_full_name)}
                    </div>
                    {!props.isViewShiftCodeSummary && (
                      <div
                        id={`TableTimeShift-Div-RemoveEmployee-${staff.employee_code}`}
                        style={{
                          color: "#db2828",
                          cursor: disabledRemove ? "" : "pointer",
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          marginTop: "0.5rem",
                        }}
                        onClick={() => {
                          if (disabledRemove) {
                            return;
                          }

                          props.onRemoveEmployee?.(staff.employee);
                        }}
                      >
                        <Icon color="red" link={!disabledRemove} name="user" />-{" "}
                        <label style={{ fontSize: "0.8rem" }}>REMOVE</label>
                      </div>
                    )}
                  </div>
                </TableCell>
                {props.listMonths.map((day) => {
                  const isHoliday = (props.holidays ?? []).some(
                    (item: HolidaysType) =>
                      item.title === HOLIDAY_TAG && item.start.isSame(day, "day")
                  );

                  const filteredEvents = staff.events.filter((item) =>
                    item.start.isSame(day, "day")
                  );
                  const hasEventOff = filteredEvents.some((item) => item.shift_status === "O");
                  const isAllOff =
                    staff.events.length > 0 &&
                    filteredEvents.every((item) => item.shift_status === "O");

                  const isReadonly = props.payrollRecordEmployeeIds.some(
                    (item) => item.employee === staff.employee && item.date.isSame(day, "days")
                  );

                  const classesHoliday = isHoliday ? "holiday" : "";
                  const classesSlotOff = hasEventOff ? "slot-off-cell" : "";
                  const classesFullOff = isAllOff ? "full-off" : "";
                  const classesReadonly =
                    isReadonly && ["ADD", "DELETE"].includes(props.selectedMode || "")
                      ? "readonly"
                      : "";

                  return (
                    <TableCell
                      key={`${staff.id}-employee-${day.format("YYYY-MM-DD")}`}
                      className={`time-shift-cell ${classesHoliday} ${classesSlotOff} ${classesFullOff} ${classesReadonly}`}
                      onClick={() => {
                        if (isReadonly) {
                          return;
                        }

                        props.onSelectSlot?.({
                          action: "click",
                          data: staff,
                          end: day,
                          slots: [day, day],
                          start: day,
                        });
                      }}
                    >
                      <CardTimeShiftCell
                        clockRecords={props.clockRecords}
                        dayInCalendar={day}
                        isReadonly={isReadonly}
                        isSelectionMode={props.selectedMode === "SELECT"}
                        isViewShiftCodeSummary={props.isViewShiftCodeSummary}
                        staff={staff}
                        onApplyNewTime={props.onApplyNewTime}
                        onApproveTime={props.onApproveTime}
                        onGetClockRecord={props.onGetClockRecord}
                        onSelectEvent={(event, e) => {
                          if (isReadonly) {
                            return;
                          }

                          props.onSelectEvent?.({ ...event, data: staff }, e);
                        }}
                      />
                    </TableCell>
                  );
                })}
                {props.showSummaryColumn ? (
                  <>
                    <TableCell
                      id={`TableTimeShift-TableCell-DayOff-${staff.employee_code}`}
                      className="summary-column"
                      rowSpan="2"
                    >
                      {shiftSummary.totalDayOff}
                    </TableCell>
                    <TableCell
                      id={`TableTimeShift-TableCell-ActualWorkHr-${staff.employee_code}`}
                      className="summary-column"
                      rowSpan="5"
                    >
                      <div style={{ marginTop: "-3.1675rem" }}>
                        {shiftSummary.approve.actualWorkHours}
                      </div>
                    </TableCell>
                    <TableCell
                      id={`TableTimeShift-TableCell-LeaveHr-${staff.employee_code}`}
                      className="summary-column"
                      rowSpan="2"
                    >
                      {shiftSummary.leaveHours}
                    </TableCell>
                    <TableCell className="summary-column" rowSpan="5">
                      <EditorColumn
                        id={`TableTimeShift-TableCell-RegularWorkHr-${staff.employee_code}`}
                        className="input-number--no-spinner"
                        style={{ marginTop: "-3.65rem" }}
                        align="left"
                        inputMin={0}
                        inputSize="small"
                        inputType="number"
                        value={staff.regular_work_hours}
                        editing
                        inputNumber
                        valueOnFocus
                        withoutStyle
                        onDataChanged={(value: string) => {
                          if (Number(value) === staff.regular_work_hours) {
                            return;
                          }

                          props.onEditRegularWork?.({
                            data: staff,
                            value: value === "" ? null : Number(value),
                          });
                        }}
                        loading={
                          !!props.buttonLoadCheck?.[
                            `${BTN_ACTS.EDIT_REGULAR_WORK}_${staff.employee}`
                          ]
                        }
                      />
                    </TableCell>
                    <TableCell
                      id={`TableTimeShift-TableCell-OTHr-${staff.employee_code}`}
                      className="summary-column"
                      colSpan="2"
                    >
                      <div
                        style={{
                          color: otHours === sumOT ? "" : "rgb(198, 0, 0)",
                          marginTop: "1.675rem",
                        }}
                      >
                        {Math.max(otHours, 0)}
                      </div>
                    </TableCell>
                    <TableCell
                      id={`TableTimeShift-TableCell-DeficitHr-${staff.employee_code}`}
                      className="summary-column"
                      rowSpan="5"
                    >
                      <div style={{ marginTop: "-3.13rem" }}>
                        {otHours < 0 ? Math.abs(otHours) : 0}
                      </div>
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
              {/*  (2) shift time row */}

              {!props.isViewShiftCodeSummary && (
                <TableRow>
                  <TableCell style={{ paddingLeft: "0.25rem" }} textAlign="left">
                    Shift time work (hr.){" "}
                  </TableCell>
                  {props.listMonths.map((day) => {
                    const isHoliday = (props.holidays ?? []).some(
                      (item: HolidaysType) =>
                        item.title === HOLIDAY_TAG && item.start.isSame(day, "day")
                    );
                    const classes = isHoliday ? "holiday" : "";

                    const workingHours = calculateWorkingHours(staff.events, day);
                    const hasHistory = hasHistoryForDay(staff.logs, day);

                    const calculatedHours = Math.max(
                      workingHours.totalHours -
                        workingHours.leaveWithShiftHours +
                        workingHours.breakLeaveOverlapHours,
                      0
                    );

                    const shiftTimeWork = Number(calculatedHours.toFixed(2));

                    let displayValue: number | string = "";

                    if (workingHours.isApprovedOff) {
                      displayValue = "OFF";
                    } else if (workingHours.hasApproved) {
                      displayValue = shiftTimeWork;
                    } else {
                      displayValue = shiftTimeWork || "";
                    }

                    return (
                      <TableCell
                        key={`${staff.id}-shift-${day.format("YYYY-MM-DD")}`}
                        id={`TableTimeShift-TableCell-Shift-${staff.employee_code}-${day.format(
                          "YYYY-MM-DD"
                        )}`}
                        className={classes}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)" }}>
                          <label />
                          <label>
                            {"\u00A0"}
                            {displayValue}
                            {"\u00A0"}
                          </label>
                          <div>
                            {hasHistory && (
                              <LoadingIcon
                                id={`TableTimeShift-LoadingIcon-WorkingHistory-${
                                  staff.employee_code
                                }-${day.format("YYYY-MM-DD")}`}
                                color="blue"
                                name="history"
                                onClick={() => {
                                  props.onOpenHistory?.({ date: day, employee: staff.employee });
                                }}
                                loading={
                                  !!props.buttonLoadCheck?.[
                                    `${staff.employee}-${day.format("YYYY-MM-DD")}`
                                  ]
                                }
                              />
                            )}
                          </div>
                        </div>
                      </TableCell>
                    );
                  })}
                  {props.showSummaryColumn ? (
                    <TableCell
                      id={`TableTimeShift-TableCell-SumOT-${staff.employee_code}`}
                      className="summary-column overtime"
                      colSpan="2"
                    >
                      Sum: {sumOT}
                    </TableCell>
                  ) : null}
                </TableRow>
              )}
              {/*  (3) actual work row */}
              {!props.isViewShiftCodeSummary && (
                <TableRow>
                  <TableCell style={{ paddingLeft: "0.25rem" }} textAlign="left">
                    Actual work (hr.){" "}
                  </TableCell>
                  {props.listMonths.map((day) => {
                    const attendance = calculateTotalAttendance(staff.events, day);

                    return (
                      <TableCell
                        key={`${staff.id}-actual-${day.format("YYYY-MM-DD")}`}
                        id={`TableTimeShift-TableCell-Actual-${staff.employee_code}-${day.format(
                          "YYYY-MM-DD"
                        )}`}
                      >
                        {attendance.hasApproved &&
                          (attendance.isAllAbsent ? (
                            <span style={{ color: "rgba(198, 0, 0, 1)" }}>ABSENT</span>
                          ) : (
                            <span>{attendance.actualWorkHours}</span>
                          ))}
                      </TableCell>
                    );
                  })}
                  {props.showSummaryColumn ? (
                    <>
                      <TableCell className="summary-column" rowSpan="3">
                        {shiftSummary.approve.totalDayOff}
                      </TableCell>
                      <TableCell className="summary-column" rowSpan="3">
                        {shiftSummary.approve.leaveHours}
                      </TableCell>
                      <TableCell className="summary-column overtime" textAlign="left">
                        {"\u00A0\u00A0"}
                        x1{" "}
                      </TableCell>
                      <TableCell className="summary-column overtime" textAlign="left">
                        {" "}
                        : {shiftSummary.approve.OTclock1hr}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              )}
              {/*  (4) OT hour row */}
              {!props.isViewShiftCodeSummary && (
                <TableRow>
                  <TableCell textAlign="left">
                    <label style={{ color: "#2185d0", paddingLeft: "0.25rem" }}>OT</label> (hr.){" "}
                  </TableCell>
                  {props.listMonths.map((day) => {
                    const attendance = calculateTotalAttendance(staff.events, day);

                    return (
                      <TableCell
                        key={`${staff.id}-ot-${day.format("YYYY-MM-DD")}`}
                        id={`TableTimeShift-TableCell-OT-${staff.employee_code}-${day.format(
                          "YYYY-MM-DD"
                        )}`}
                      >
                        {attendance.hasApproved ? <span>{attendance.overtimeHours}</span> : ""}
                      </TableCell>
                    );
                  })}
                  {props.showSummaryColumn ? (
                    <>
                      <TableCell className="summary-column overtime" textAlign="left">
                        {"\u00A0\u00A0"}x1.5
                      </TableCell>
                      <TableCell className="summary-column overtime" textAlign="left">
                        : {shiftSummary.approve.OTclock1_5hr}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              )}
              {/*  (5) Leave hour row */}
              {!props.isViewShiftCodeSummary && (
                <TableRow>
                  <TableCell textAlign="left">
                    <label style={{ color: "#db2828", paddingLeft: "0.25rem" }}>Leave</label> (hr.){" "}
                  </TableCell>
                  {props.listMonths.map((day) => {
                    const workingHours = calculateWorkingHours(staff.events, day);

                    return (
                      <TableCell
                        key={`${staff.id}-leave-${day.format("YYYY-MM-DD")}`}
                        id={`TableTimeShift-TableCell-Leave-${staff.employee_code}-${day.format(
                          "YYYY-MM-DD"
                        )}`}
                      >
                        {workingHours.hasApproved ? (
                          <span
                            style={{
                              color: workingHours.leaveHours > 0 ? "rgba(198, 0, 0, 1)" : "",
                            }}
                          >
                            {workingHours.leaveHours}
                          </span>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    );
                  })}
                  {props.showSummaryColumn ? (
                    <>
                      <TableCell className="summary-column overtime" textAlign="left">
                        {"\u00A0\u00A0"}x3
                      </TableCell>
                      <TableCell className="summary-column overtime" textAlign="left">
                        : {shiftSummary.approve.OTclock3hr}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              )}
              {/* (6) Total slot */}
              {props.isViewShiftCodeSummary && (
                <>
                  <TableRow className="view-summary total-slot">
                    <TableTotalSlotsCell
                      style={{ opacity: 1, position: "absolute", top: 4 }}
                      cellStyle={{ borderTop: "none", padding: 0, zIndex: 2 }}
                      colSpan={1}
                      groupedEvents={groupedEvents}
                      remainingCells={props.listMonths.length}
                      totalSlots={totalSlots}
                    />
                  </TableRow>
                  <TableRow className="view-summary total-slot">
                    <TableTotalSlotsCell
                      cellStyle={{ borderBottom: "none" }}
                      colSpan={props.listMonths.length + 1}
                      groupedEvents={groupedEvents}
                      remainingCells={0}
                      totalSlots={totalSlots}
                    />
                  </TableRow>
                </>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

TableTimeShift.displayName = "TableTimeShift";

export default React.memo(TableTimeShift);
